import React from "react";
import Icon from "../../../components/AppIcon";
import { useNavigate } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const links = [
    {
      title: "Discover",
      items: [
        { name: "Trek Discovery Hub", href: "/trek-discovery-hub" },
        { name: "Activities & Tours", href: "/activities" },
        { name: "Sherpa Guide Profiles", href: "/sherpa-guide-profiles" },
        { name: "Traveler Stories", href: "/traveler-stories-hub" },
        { name: "Book Consultation", href: "/booking-consultation-center" },
      ],
    },
    {
      title: "Nepal Info",
      items: [
        { name: "Nepal Visa Information", href: "/nepal-visa-information" },
        { name: "Travel Information", href: "/travel-information" },
        { name: "Getting to Nepal", href: "/getting-to-nepal" },
        { name: "Weather & Climate", href: "/weather-climate" },
        { name: "Nepal FAQ", href: "/nepal-faq" },
      ],
    },
    {
      title: "Company",
      items: [
        { name: "About Us", href: "#" },
        { name: "Our Mission", href: "#" },
        { name: "Sustainability", href: "#" },
        { name: "Careers", href: "#" },
      ],
    },
  ];

  const socials = [
    { icon: "Facebook", href: "#", label: "Facebook" },
    { icon: "Instagram", href: "#", label: "Instagram" },
    { icon: "Twitter", href: "#", label: "Twitter" },
    { icon: "Youtube", href: "#", label: "YouTube" },
  ];

  return (
    <footer className="bg-gray-900 text-white">

      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-headline text-2xl font-bold mb-2">
                Stay Connected to the Mountains
              </h3>
              <p className="text-white/70">
                Get exclusive trek insights, cultural stories, and early access to new expeditions.
              </p>
            </div>
            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex flex-col sm:flex-row gap-3"
            >
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary/60 text-sm"
              />
              <button
                type="submit"
                className="flex items-center gap-2 px-5 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors text-sm flex-shrink-0"
              >
                <Icon name="Send" size={15} />
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand */}
          <div className="col-span-2">
            {/* Logo with accent */}
            <a href="/" className="flex items-center gap-3 mb-5 group w-fit">
              <div className="relative">
                <div className="w-11 h-11 rounded-xl overflow-hidden ring-2 ring-primary/30 group-hover:ring-primary/60 transition-all">
                  <img src="/assets/images/logo.jpeg" alt="Sherpa Trekker" className="w-full h-full object-cover" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-sunrise-gold rounded-full border-2 border-gray-900" />
              </div>
              <div>
                <span className="font-headline font-bold text-lg text-white block">Sherpa Trekker</span>
                <span className="text-xs text-white/50">Where Adventure Meets Authenticity</span>
              </div>
            </a>

            <p className="text-white/65 text-sm leading-relaxed mb-6 max-w-xs">
              Guided by generations of Sherpa wisdom, we create transformative Himalayan experiences that honor both adventure and authentic cultural immersion.
            </p>

            {/* Contact */}
            <div className="space-y-2.5 mb-6">
              {[
                { icon: "MapPin", text: "Thamel, Kathmandu, Nepal" },
                { icon: "Phone", text: "+977-9851157213" },
                { icon: "Mail", text: "imsherpatrekker@gmail.com" },
              ].map((c, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-white/65">
                  <Icon name={c.icon} size={14} className="text-primary flex-shrink-0" />
                  {c.text}
                </div>
              ))}
            </div>

            {/* Socials */}
            <div className="flex gap-2">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="w-9 h-9 bg-white/8 hover:bg-primary rounded-lg flex items-center justify-center transition-colors duration-200"
                >
                  <Icon name={s.icon} size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {links.map((section) => (
            <div key={section.title}>
              <h4 className="font-semibold text-white text-sm mb-4">{section.title}</h4>
              <ul className="space-y-2.5">
                {section.items.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className="text-sm text-white/60 hover:text-white transition-colors duration-200"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Emergency */}
        <div className="mt-12 p-6 bg-white/5 rounded-2xl border border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon name="Phone" size={20} className="text-red-400" />
              </div>
              <div>
                <p className="font-semibold text-white">24/7 Emergency Support</p>
                <p className="text-sm text-white/60">Always here when you need us most</p>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <p className="font-headline font-bold text-sunrise-gold text-lg">+977-9851157213</p>
              <p className="text-sm text-white/60">imsherpatrekker@gmail.com</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-white/50">
            © {year} Sherpa Trekker. All rights reserved.
          </p>
          <div className="flex gap-5 text-sm text-white/50">
            {["Privacy Policy", "Terms of Service", "Cookie Policy"].map((t) => (
              <a key={t} href="#" className="hover:text-white transition-colors">{t}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
